/**
 * El SVG del cuerpo de una mascota: leerlo, sanearlo y saber qué colores tiene.
 *
 * Un SVG subido puede traer cualquier cosa (scripts, links, imágenes externas),
 * así que nunca se inyecta: se parsea a un árbol con una lista blanca de
 * elementos y atributos de dibujo, y React dibuja ese árbol. Al subirlo desde el
 * admin se guarda ya saneado (`serializeSvg`), en el mismo formato que escribe
 * scripts/trace-mascot.py.
 *
 * Los colores se normalizan a hex de 6 dígitos en minúsculas: son las claves de
 * `Mascota.colors`, lo que permite repintar cualquier SVG sin saber qué es cada
 * forma. Entiende `fill`/`stroke` como atributo, en `style` y en reglas de
 * clase de un `<style>` (lo que exporta Illustrator).
 */

export type SvgNode = {
  tag: string;
  attrs: Record<string, string>;
  children: SvgNode[];
};

export type ParsedSvg = {
  viewBox: [number, number, number, number];
  root: SvgNode[];
  /** Colores distintos del dibujo, del más usado al menos usado. */
  colors: string[];
};

const TAGS = new Set(['g', 'path', 'circle', 'ellipse', 'rect', 'polygon', 'polyline', 'line']);
const ATTRS = new Set([
  'd', 'points', 'cx', 'cy', 'r', 'rx', 'ry', 'x', 'y', 'x1', 'y1', 'x2', 'y2', 'width', 'height',
  'transform', 'fill', 'fill-rule', 'clip-rule', 'fill-opacity', 'stroke', 'stroke-width',
  'stroke-linecap', 'stroke-linejoin', 'stroke-opacity', 'opacity',
]);
const PAINT = ['fill', 'stroke'] as const;

/** '#abc', '#aabbcc', 'rgb(1,2,3)' → '#aabbcc'. Otra cosa (none, url(), nombres) → null. */
export function normalizeColor(value: string): string | null {
  const v = value.trim().toLowerCase();
  let m = /^#([0-9a-f]{3})$/.exec(v);
  if (m) return '#' + [...m[1]].map((c) => c + c).join('');
  m = /^#([0-9a-f]{6})$/.exec(v);
  if (m) return v;
  m = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/.exec(v);
  if (m) return '#' + m.slice(1, 4).map((n) => Math.min(255, +n).toString(16).padStart(2, '0')).join('');
  return null;
}

/** `fill:#fff;stroke:red` → { fill: '#fff', stroke: 'red' } */
function parseDecls(css: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const decl of css.split(';')) {
    const i = decl.indexOf(':');
    if (i > 0) out[decl.slice(0, i).trim().toLowerCase()] = decl.slice(i + 1).trim();
  }
  return out;
}

/** Reglas `.st0{fill:#123}` y `.a,.b{…}` de los <style> del documento. */
function classRules(doc: Document): Map<string, Record<string, string>> {
  const rules = new Map<string, Record<string, string>>();
  for (const style of Array.from(doc.getElementsByTagName('style'))) {
    const text = style.textContent ?? '';
    for (const m of text.matchAll(/([^{}]+)\{([^}]*)\}/g)) {
      const decls = parseDecls(m[2]);
      for (const sel of m[1].split(',')) {
        const cls = /^\s*\.([\w-]+)\s*$/.exec(sel)?.[1];
        if (cls) rules.set(cls, { ...rules.get(cls), ...decls });
      }
    }
  }
  return rules;
}

export function parseSvg(text: string): ParsedSvg {
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const svg = doc.documentElement;
  if (svg.nodeName !== 'svg' || doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error('No es un SVG válido');
  }

  const vbAttr = svg.getAttribute('viewBox')?.trim().split(/[\s,]+/).map(Number);
  const w = parseFloat(svg.getAttribute('width') ?? '');
  const h = parseFloat(svg.getAttribute('height') ?? '');
  const viewBox: [number, number, number, number] | null =
    vbAttr?.length === 4 && vbAttr.every(Number.isFinite) ? [vbAttr[0], vbAttr[1], vbAttr[2], vbAttr[3]]
      : Number.isFinite(w) && Number.isFinite(h) ? [0, 0, w, h]
        : null;
  if (!viewBox || viewBox[2] <= 0 || viewBox[3] <= 0) {
    throw new Error('El SVG no dice su tamaño (le falta viewBox o width/height)');
  }

  const rules = classRules(doc);
  const count = new Map<string, number>();

  const walk = (el: Element): SvgNode | null => {
    const tag = el.localName;
    if (!TAGS.has(tag)) return null;
    const attrs: Record<string, string> = {};
    for (const a of Array.from(el.attributes)) {
      if (ATTRS.has(a.name)) attrs[a.name] = a.value;
    }
    // Clase y style ganan sobre el atributo, como en CSS.
    for (const cls of (el.getAttribute('class') ?? '').split(/\s+/)) {
      for (const [k, v] of Object.entries(rules.get(cls) ?? {})) if (ATTRS.has(k)) attrs[k] = v;
    }
    for (const [k, v] of Object.entries(parseDecls(el.getAttribute('style') ?? ''))) {
      if (ATTRS.has(k)) attrs[k] = v;
    }
    for (const p of PAINT) {
      if (!attrs[p]) continue;
      const c = normalizeColor(attrs[p]);
      if (c) {
        attrs[p] = c;
        count.set(c, (count.get(c) ?? 0) + 1);
      } else if (attrs[p] !== 'none') {
        delete attrs[p]; // url(#degradé) y compañía: no se dibuja con defs
      }
    }
    const children = Array.from(el.children).map(walk).filter((n): n is SvgNode => n !== null);
    if (tag === 'g' && children.length === 0) return null;
    return { tag, attrs, children };
  };

  const root = Array.from(svg.children).map(walk).filter((n): n is SvgNode => n !== null);
  if (root.length === 0) throw new Error('El SVG no tiene nada que dibujar');
  const colors = [...count.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c);
  return { viewBox, root, colors };
}

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

/** El SVG saneado, listo para subir: solo lo que `parseSvg` deja pasar. */
export function serializeSvg({ viewBox, root }: ParsedSvg): string {
  const node = (n: SvgNode): string => {
    const attrs = Object.entries(n.attrs).map(([k, v]) => ` ${k}="${esc(v)}"`).join('');
    return n.children.length
      ? `<${n.tag}${attrs}>${n.children.map(node).join('')}</${n.tag}>`
      : `<${n.tag}${attrs}/>`;
  };
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox.join(' ')}">${root.map(node).join('')}</svg>`;
}

// Una descarga por URL, compartida por todas las mascotas que la usan.
const cache = new Map<string, Promise<ParsedSvg>>();

export function loadSvg(url: string): Promise<ParsedSvg> {
  let p = cache.get(url);
  if (!p) {
    p = fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`${url} → ${r.status}`);
        return r.text();
      })
      .then(parseSvg);
    p.catch(() => cache.delete(url)); // que se pueda reintentar
    cache.set(url, p);
  }
  return p;
}
